/**
 * Shared utilities for stream/channel display (Preview area and Dashboard).
 */

/**
 * Format a stream label for display (truncated).
 * @param {Record<string, string>} streamLabels - Map of streamId -> label
 * @param {string} streamId - Stream ID
 * @param {number} idx - Slot index (0-based)
 * @returns {string}
 */
export function getStreamLabel(streamLabels, streamId, idx) {
  const label = streamLabels?.[streamId];
  if (label && label.trim()) return label.length > 40 ? `${label.slice(0, 37)}...` : label;
  if (streamId) {
    const short = streamId.includes('#') ? streamId.split('#')[1] : streamId;
    return short.length > 20 ? `${short.slice(0, 17)}...` : short;
  }
  return `Stream ${idx + 1}`;
}

/**
 * HA channel border color based on state.
 * @param {boolean} isDown - No streams active
 * @param {boolean} isDegraded - Fewer streams than expected
 * @param {{ singleChannelOffline?: boolean }} [opts] - When true, single-channel with no publisher uses amber (not red)
 * @returns {string} Tailwind border class
 */
export function getHABorderColor(isDown, isDegraded, { singleChannelOffline = false } = {}) {
  if (singleChannelOffline) return 'border-amber-500';
  if (isDown) return 'border-red-500';
  if (isDegraded) return 'border-amber-500';
  return 'border-violet-500';
}

/**
 * Stats viewer URL for a channel within a dashboard.
 * @param {string | null} dashboardAlias - Dashboard alias (short URL identifier)
 * @param {string | null} channelId - Full channel ID
 * @returns {string | null}
 */
export function getStatsUrl(dashboardAlias, channelId) {
  if (!dashboardAlias || !channelId) return null;
  return `/stats?alias=${encodeURIComponent(dashboardAlias)}&channelId=${encodeURIComponent(channelId)}`;
}

/**
 * Portal URL for a channel.
 * @param {string} channelId - Channel ID (e.g. region#appId#channelAlias)
 * @returns {string}
 */
export function getPortalUrl(channelId) {
  const appId = channelId.split('#')[1] || 'us';
  return `https://phenixrts.com/portal/${appId}/channels/${encodeURIComponent(channelId)}/streams/`;
}
